import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import useSnackBar from "../../../../components/Commons/SnackBar/useSnackBar";
import { useCompanyStore } from "../../../../store/AuthStore";
import { useEffect, useState } from "react";
import { GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { Header } from "../../../../components/Header";
import DataTable from "../../../../components/Tables/GridTableMaterialUI/DataTable";
import { useProjectsColumns } from "./columns";
import { Project } from "../../../../interfaces/Projects/projects";
import { FilterTable } from "../../../../components/Filter";
import { Box } from "@mui/material";
import { usePreferenceTypesStore } from "../../../../store/Preferences/PreferencesStore";
import { useGridPreferences } from "../../../../hooks/useGridPreferences";
import { PreferenceType } from "../../../../interfaces/Commons/Preferences/preferences";
import { Parameter } from "../../../../interfaces/Commons/parameters";
import { useFormulaEvaluator } from "../../../../hooks/useFormulaEvaluator";
import { useValidateCentinelValue } from "../../../Projects/Projects/components/helpers/hookValidateCentinelValue/hookValidateCentinelValue";
import GenericPromises from "../../../../api/GenericPromises";
import DataTableSticky from "../../../../components/Tables/GridTableMaterialUI/DataTableSticky";
import { Spinner } from "../../../../components/Commons/Spinner/Spinner";

interface DataObject {
    [key: string]: any;
}

export const TableAnalysisProjects = () => {
    const [t] = useTranslation("global");
    const navigate = useNavigate();
    const location = useLocation();
    const { showSnackBar, SnackbarComponent } = useSnackBar();
    const { companyState } = useCompanyStore();
    const [isLoadingDataTable, setIsLoadingDataTable] = useState(false);
    const [unauthorized, setUnauthorized] = useState(true);
    const [projectsData, setProjectsData] = useState<GridRowsProp>([]);
    const [messageSnack, setMessageSnack] = useState("");
    const [myPreferences, setMyPreferences] = useState({});
    const [isLoadedPreference, setIsLoadedPreference] = useState(false);
    const { createColumnsFromData } = useProjectsColumns();
    const [columns, setColumns] = useState<GridColDef<Project>[]>([]);
    const {
        LoadColumnsOrderProjects,
        LoadColumnsVisible,
        LoadColumnsSort,
        VerifyPreferenceOrderByResourceRoute,
        VerifyPreferenceVisualByResourceRoute,
        VerifyPreferenceSortByResourceRoute } = useGridPreferences();
    const [mySortPreference, setMySortPreference] = useState([]);
    const { preferenceTypes } = usePreferenceTypesStore.getState();
    const initFilter = '/reportprojects?filter=';
    const { EvaluateFormula, fetchParameters } = useFormulaEvaluator();
    const [comboParameters, setComboParameters] = useState<Parameter[]>([]);
    const { HasCentinelValue, GetNameParamToReplace, HasValidateResponse } = useValidateCentinelValue();
    const { GenericGetResource } = GenericPromises();

    const loadTablePreferences = async (data: any) => {
        // if exists preferencetypes
        if (preferenceTypes.length) {
            // if exists order preferences
            let myPreferencesColumnsOrder = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsOrder");
            if (myPreferencesColumnsOrder) {
                try {
                    const preference = await VerifyPreferenceOrderByResourceRoute(
                        "/reportprojects",
                        myPreferencesColumnsOrder.preference_type_id ?? 0
                    );
                    if (preference) {
                        const columnsData = await createColumnsFromData(data);
                        if (columnsData) {
                            const [fixedColumns, dynamicColumns] = columnsData;
                            if (!preference.preference_value.some((item) => item.column_name === "subtotal_sales")) {
                                preference.preference_value.push({ "column_name": "subtotal_sales", "column_type": "numeric" });
                            }

                            if (!preference.preference_value.some((item) => item.column_name === "subtotal_purchases")) {
                                preference.preference_value.push({ "column_name": "subtotal_purchases", "column_type": "numeric" });
                            }

                            if (!preference.preference_value.some((item) => item.column_name === "subtotal_amount")) {
                                preference.preference_value.push({ "column_name": "subtotal_amount", "column_type": "numeric" });
                            }

                            if (!preference.preference_value.some((item) => item.column_name === "total_sales")) {
                                preference.preference_value.push({ "column_name": "total_sales", "column_type": "numeric" });
                            }

                            if (!preference.preference_value.some((item) => item.column_name === "total_purchases")) {
                                preference.preference_value.push({ "column_name": "total_purchases", "column_type": "numeric" });
                            }

                            if (!preference.preference_value.some((item) => item.column_name === "total_amount")) {
                                preference.preference_value.push({ "column_name": "total_amount", "column_type": "numeric" });
                            }

                            let myColumns = await LoadColumnsOrderProjects("/reportprojects", fixedColumns, preference?.preference_value, dynamicColumns);
                            myColumns.map((element) => {
                                if (element.field === "subtotal_amount" || element.field === "total_amount") {
                                    element.minWidth = 300;
                                } else {
                                    element.minWidth = 200
                                }
                            })
                            setColumns(myColumns);
                        }
                    }
                } catch (error) {
                    showSnackBar(`Error fetching column order preferences:${error}`, "error");
                }
            }

            let myPreferencesColumnsVisual = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsVisibility");
            // if exists visual preferences
            if (myPreferencesColumnsVisual) {
                try {
                    const preference = await VerifyPreferenceVisualByResourceRoute(
                        "/reportprojects",
                        myPreferencesColumnsVisual.preference_type_id ?? 0
                    );
                    if (preference) {
                        let myLocalPreferences = await LoadColumnsVisible("/reportprojects", preference);
                        setMyPreferences(myLocalPreferences);
                    }
                } catch (error) {
                    console.error("Error fetching column visual preferences:", error);
                }
            }

            let myPreferencesColumnsSort = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsSorting");
            // if exists sort preferences
            if (myPreferencesColumnsSort) {
                try {
                    const preference = await VerifyPreferenceSortByResourceRoute(
                        "/reportprojects",
                        myPreferencesColumnsSort.preference_type_id ?? 0
                    );
                    if (preference) {
                        let myLocalPreferences = await LoadColumnsSort("/reportprojects", preference);
                        setMySortPreference(myLocalPreferences);
                        setIsLoadedPreference(true);
                    }
                } catch (error) {
                    console.error("Error fetching column sort preferences:", error);
                }
            }

        }
    }

    const replaceFormulas = async (data: DataObject[], myLocalParameters?: Parameter[]): Promise<DataObject[]> => {
        return Promise.all(
            data.map(async (item) => {
                await Promise.all(
                    Object.keys(item).map(async (key) => {
                        if (key.startsWith('type_') && String(item[key]).toLowerCase() === 'formula') {
                            const field = key.substring(5);
                            const valueKey = `value_${field}`;
                            if (valueKey in item) {
                                try {
                                    const responseProjectExtrafields = await GenericGetResource(
                                        `/projectextrafields/byprojectid/${item["project_id"]}`
                                    );
                                    let myComboParameters: Parameter[] = []
                                    //Si hay algo en el combo agarre los parametros de este
                                    if (comboParameters.length > 0) {
                                        myComboParameters = comboParameters
                                    }
                                    //Si no hay algo en el combo agarre el parametro local y si no que los parametros esten vacios
                                    else if (myLocalParameters && myLocalParameters.length > 0) {
                                        myComboParameters = myLocalParameters
                                    }
                                    const evaluatedValue = await EvaluateFormula(
                                        item[valueKey] ?? '',
                                        responseProjectExtrafields.data.data ?? [],
                                        myComboParameters
                                    );
                                    if (!HasValidateResponse(evaluatedValue) && HasCentinelValue(field ?? '') && !(item["project_is_closed"])) {
                                        // calcula el valor con hoy
                                        const now = new Date();
                                        const isoString = now.toISOString();
                                        await EvaluateFormula(`${item[valueKey].replace(`{${GetNameParamToReplace(field ?? '')}}`, `new Date("${isoString}")`)}`, responseProjectExtrafields.data.data ?? [], myComboParameters)
                                            .then(async (responseExtraField) => {
                                                if (responseExtraField > 0) {
                                                    item[valueKey] = responseExtraField;
                                                }
                                                else {
                                                    item[valueKey] = evaluatedValue;
                                                }
                                            });
                                    }
                                    else {
                                        item[valueKey] = evaluatedValue;
                                    }

                                } catch (error) {
                                    item[valueKey] = t("projectextrafields.info.no-calculated");
                                }
                            }
                        }
                    })
                );
                return item;
            })
        );
    }

    const onApplyFilter = async (data: any) => {
        try {
            if (data && data.length && data.length > 0) {
                let myParameters: Parameter[] = [];
                if (comboParameters.length === 0) {
                    myParameters = await fetchParameters();
                    await setComboParameters(() => myParameters);
                }
                else {
                    myParameters = comboParameters;
                }
                await loadTablePreferences(data);
                let myCalculatedValues = await replaceFormulas(data, myParameters);
                await setProjectsData((prev) => myCalculatedValues);
                setIsLoadingDataTable(false);
            }
            else {
                setProjectsData([])
                setIsLoadingDataTable(false);
            }
        } catch (error) {
            showSnackBar("error", "error");
        }
    }

    useEffect(() => {
        const loadInfo = async () => {
            document.title = `${companyState?.comercial_name} - ${t("reportprojects.title")}`;
            if (location.state !== null) {
                if (location.state.row && location.state.row.purchase_invoice_id) {
                    navigate("/projects/view", { state: { row: location.state.row } });
                }
            }
            await loadTablePreferences([])
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }
        loadInfo()
    }, []);

    useEffect(() => { }, [columns])

    return (
        <>
            <Header
                size="no_title"
                child={
                    <>
                        <div className='d-flex justify-content-between'>
                            <h3>{t("reportprojects.title")}</h3>
                            <FilterTable
                                setIsLoadingDataTable={setIsLoadingDataTable}
                                setDataTable={setProjectsData}
                                showSnackBar={showSnackBar}
                                columnsTable={columns}
                                initFilter={initFilter}
                                onApplyFilter={onApplyFilter}
                                resourceRoute="/reportprojects"
                            />
                        </div>
                    </>
                }
            />
            {!unauthorized && <div className='screen-container d-flex flex-column justify-content-center align-items-center'> <img alt='ERROR' style={{ height: "20rem", width: "20rem" }} src={require("../../../../assets/img/error.webp")} /> <h2>{messageSnack}</h2> </div>}
            <div className="d-flex justify-content-center">
                {(isLoadedPreference) ?
                    <Box sx={{ height: 'calc(100vh - 180px)', width: '100%' }}>
                        <DataTableSticky
                            columns={columns}
                            setColumns={setColumns}
                            data={projectsData ?? []}
                            entityId={"project_id"}
                            entity={`ReportProjects`}
                            preferences={myPreferences}
                            namePreferences={"/reportprojects"}
                            nameOrderColumns={"/reportprojects"}
                            isLoading={isLoadingDataTable}
                            isChildren={true}
                            sortPreference={mySortPreference}
                            countColumns={["subtotal_sales", "subtotal_purchases", "subtotal_amount", "total_sales", "total_purchases", "total_amount"]}
                            selectRows={true}
                        />
                    </Box>
                    :
                    <Spinner isBox={false} />
                }
            </div>
            <SnackbarComponent />
        </>
    )
}
